import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import ParagraphList from "./ParagraphList";
import ParagraphBox from "./ParagraphBox";
import CloseButton from "../../../../../components/CloseButton";
import ReviewSettingsPanel from "../../../../../components/ReviewSettingsPanel";
import { openOfficialBookPreview } from "../../../../official-books/OfficialBookPage";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 반응형: PC 좌우 분할 → 모바일 상하 분할 (WorksheetModalV2.css)
 * 2. 모의고사 복습 설정: 압축 체크박스 바 → 단어테스트와 동일한
 *    풀 패널(ReviewSettingsPanel)로 교체. 지문 미리보기 아래 스크롤 영역에 배치.
 * 3. 하단 바는 이전/다음 버튼만.
 */
// 따라 말하기에서 다음 문장으로 넘어가는 점수로 고를 수 있는 값
const SPEAKING_PASS_SCORE_LIST = [50, 60, 70, 80, 90];
// 구문분석 난이도. 지문분석 출제(TypeSelectionPage)와 같은 말이다
const SYNTAX_LEVEL_OPTION_LIST = [
  { key: "elementary", label: "하 · SVOC, 범위 주어짐" },
  { key: "intermediate", label: "중 · SVOC, 범위 직접" },
  { key: "advanced", label: "상 · 모든 성분" },
];

const SyntaxLevelSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(event) => onChange(event.target.value)}
    aria-label="구문분석 난이도"
    style={{ padding: "4px 8px", borderRadius: "8px", border: "1px solid #D5DCE0", fontFamily: "inherit", fontSize: "14px" }}
  >
    {SYNTAX_LEVEL_OPTION_LIST.map((one) => (
      <option key={one.key} value={one.key}>
        {one.label}
      </option>
    ))}
  </select>
);

// 파닉스북에서 학생이 지나는 단계. 고른 것만 섹션이 된다(api/assignment/phonics_book.py 의 PHONICS_STEP_LIST 와 같은 값)
export const PHONICS_STEP_LIST = [
  { key: "mouth", label: "입모양 영상" },
  { key: "story", label: "이야기 듣기" },
  { key: "speak", label: "이야기 따라 말하기" },
  { key: "syntax", label: "구문분석" },
  { key: "book", label: "책 넘겨 보기" },
  { key: "quiz", label: "듣기 · 말하기" },
];

// 리더스북에 넣을 섹션(api/assignment/views.py 의 READERS_SECTION_KEY_LIST 와 같은 값). 단원은 'unit-번호' 로 따로 더한다
export const READERS_SECTION_LIST = [
  { key: "activity", label: "문제" },
  { key: "project", label: "프로젝트" },
  { key: "study", label: "단어장" },
  { key: "matching", label: "짝 맞추기" },
  { key: "exam", label: "단어 시험" },
];
export const READERS_VOCABULARY_SECTION_KEY_LIST = ["study", "matching", "exam"];

const OptionRow = ({ label, children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 16px" }}>
    <span style={{ width: "52px", flex: "none", fontSize: "13px", fontWeight: 700, color: "#7A8490" }}>{label}</span>
    {children}
  </div>
);

const OptionCheck = ({ label, checked, onChange }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      style={{ width: "18px", height: "18px" }}
    />
    {label}
  </label>
);

const ParagraphSelectionPage = ({
  worksheet,
  onClose,
  onClickPrev,
  onClickNext,
  selectedTypes,
  selectedParagraphs,
  setSelectedParagraphs,
  isRetryEnabled,
  setIsRetryEnabled,
  isAutoRetryEnabled,
  setIsAutoRetryEnabled,
  maxRetries,
  setMaxRetries,
  isSpeakingEnabled,
  setIsSpeakingEnabled,
  speakingPassScore,
  setSpeakingPassScore,
  phonicsStepList,
  setPhonicsStepList,
  isSpeakingItemExcluded,
  setIsSpeakingItemExcluded,
  syntaxLevel,
  setSyntaxLevel,
  isSyntaxEnabled,
  setIsSyntaxEnabled,
  readersSectionList,
  setReadersSectionList,
}) => {
  const { user } = useSelector((state) => state.auth);
  // 공식 교재(OfficialBookPage 가 '지인에듀' 로 연다)만 학생 화면 미리보기가 된다
  const isOfficialBook = worksheet.type === "readers_book" && worksheet.author_name === "지인에듀";
  const toggleReadersSection = (key, checked) =>
    setReadersSectionList(checked ? [...readersSectionList.filter((one) => one !== key), key] : readersSectionList.filter((one) => one !== key));

  const worksheetData = useMemo(() => {
    if (worksheet.type === "comprehensive") {
      const examIndex =
        selectedTypes.find((type) => type.key === "comprehensive")?.index || 1;
      return worksheet.data[examIndex - 1];
    } else {
      return worksheet.data;
    }
  }, [worksheet.type, worksheet.data, selectedTypes]);

  const [activeParagraph, setActiveParagraph] = useState(worksheetData[0]);

  useEffect(() => {
    setSelectedParagraphs(worksheetData.map((_, i) => i));
  }, [setSelectedParagraphs, worksheetData]);

  return (
    <div
      className="ws-paragraph-layout"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      <CloseButton onClick={onClose} />

      {/* 왼쪽: 지문 목록 */}
      <ParagraphList
        worksheetData={worksheetData}
        worksheetTitle={worksheet.title}
        activeParagraph={activeParagraph}
        setActiveParagraph={setActiveParagraph}
        selectedParagraphs={selectedParagraphs}
        setSelectedParagraphs={setSelectedParagraphs}
      />

      {/* 오른쪽: 지문 내용 + 복습 패널 + 하단 바 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <div
          className="ws-scroll"
          style={{ flex: 1, minHeight: 0, padding: "10px 16px", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
        >
          {/* 리더스북 · 파닉스북에 넣을 것. 긴 지문 아래 묻혀 안 보여서 지문 위 판으로 올렸다(2026-10-20 대표) */}
          {/* ECHO 발음기호 과제는 고를 것이 없다(기호 영상 · 기호 테스트 둘 다 낸다) */}
          {worksheet.type === "readers_book" && !worksheet.ipa_set && (
            <div style={{ margin: "4px 0 14px", padding: "12px 14px", border: "1px solid #E1E7EB", borderRadius: "12px", background: "#F7FAFB" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "13px", fontWeight: 800, color: "#5B6670" }}>출제할 것</span>
                {isOfficialBook && (
                  <button
                    type="button"
                    onClick={() => openOfficialBookPreview(worksheet.id, user)}
                    style={{ padding: "5px 12px", border: "1px solid #D5DCE0", borderRadius: "999px", background: "#FFFFFF", fontFamily: "inherit", fontSize: "13px", fontWeight: 700, color: "#0E141B", cursor: "pointer" }}
                  >
                    학생 화면 미리보기
                  </button>
                )}
              </div>
              {worksheet.type === "readers_book" && worksheet.is_phonics_book && (
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 18px", margin: 0 }}>
                  {PHONICS_STEP_LIST.map((step) => (
                    <label
                      key={step.key}
                      style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={phonicsStepList.includes(step.key)}
                        onChange={(event) =>
                          setPhonicsStepList(
                            event.target.checked
                              ? PHONICS_STEP_LIST.map((one) => one.key).filter((key) => key === step.key || phonicsStepList.includes(key))
                              : phonicsStepList.filter((key) => key !== step.key)
                          )
                        }
                        style={{ width: "18px", height: "18px" }}
                      />
                      {step.label}
                    </label>
                  ))}
                  {phonicsStepList.includes("syntax") && <SyntaxLevelSelect value={syntaxLevel} onChange={setSyntaxLevel} />}
                  {phonicsStepList.includes("quiz") && (
                    <OptionCheck label="말하기 문항 빼기" checked={Boolean(isSpeakingItemExcluded)} onChange={setIsSpeakingItemExcluded} />
                  )}
                </div>
              )}
              {/* 문법책 · 단어장은 고를 것 없이 책 한 권을 그대로 낸다 */}
              {worksheet.type === "readers_book" && !worksheet.is_phonics_book && !worksheet.is_companion_book && (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {/* 단원별로 낼 수 있다. 끈 단원은 본문 · 단어장 · 짝 맞추기가 빠진다(2026-10-20 대표) */}
                  {(worksheet.unit_list || []).length > 1 && (
                    <OptionRow label="단원">
                      {worksheet.unit_list.map((unit) => (
                        <OptionCheck
                          key={unit.number}
                          label={`Unit ${unit.number}${unit.title ? ` · ${unit.title}` : ""}`}
                          checked={readersSectionList.includes(`unit-${unit.number}`)}
                          onChange={(checked) => toggleReadersSection(`unit-${unit.number}`, checked)}
                        />
                      ))}
                    </OptionRow>
                  )}
                  {/* 단어 셋은 지인에듀가 단원 단어장을 넣은 책, 구문분석은 단원 본문을 구문분석해 둔 책만 실제로 생긴다 */}
                  <OptionRow label="넣을 것">
                    {READERS_SECTION_LIST.filter((one) => !one.key.startsWith("unit-")).map((one) => (
                      <OptionCheck
                        key={one.key}
                        label={one.label}
                        checked={readersSectionList.includes(one.key)}
                        onChange={(checked) => toggleReadersSection(one.key, checked)}
                      />
                    ))}
                    <OptionCheck label="구문분석" checked={Boolean(isSyntaxEnabled)} onChange={setIsSyntaxEnabled} />
                    {isSyntaxEnabled && <SyntaxLevelSelect value={syntaxLevel} onChange={setSyntaxLevel} />}
                  </OptionRow>
                  <OptionRow label="말하기">
                    <OptionCheck label="따라 말하기 점수 넣기" checked={Boolean(isSpeakingEnabled)} onChange={setIsSpeakingEnabled} />
                    {/* 이 점수 이상 읽으면 다음 문장으로 넘어간다(2026-10-18 대표) */}
                    {isSpeakingEnabled && (
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 600 }}>
                        다음 문장으로
                        <select
                          value={speakingPassScore}
                          onChange={(event) => setSpeakingPassScore(Number(event.target.value))}
                          style={{ padding: "4px 8px", borderRadius: "8px", border: "1px solid #D5DCE0", fontFamily: "inherit", fontSize: "14px" }}
                        >
                          {SPEAKING_PASS_SCORE_LIST.map((score) => (
                            <option key={score} value={score}>
                              {score}점
                            </option>
                          ))}
                        </select>
                        이상
                      </label>
                    )}
                  </OptionRow>
                </div>
              )}
            </div>
          )}

          {activeParagraph ? (
            <ParagraphBox paragraph={activeParagraph} type={worksheet.type} />
          ) : (
            <p style={{ color: "#7A8490", padding: "14px" }}>
              목록에서 지문을 눌러주세요.
            </p>
          )}

          {/* 모의고사만 여기 둔다. 유형 선택 화면이 없어서 이 화면이 마지막 자리다.
              나머지 유형은 `TypeSelectionPage` 에 같은 패널이 있다. */}
          {worksheet.type === "paragraph" && (
            <div style={{ margin: "6px 0 16px", maxWidth: "560px" }}>
              <ReviewSettingsPanel
                isRetryEnabled={isRetryEnabled}
                onToggleRetry={() => setIsRetryEnabled(!isRetryEnabled)}
                isAutoRetryEnabled={isAutoRetryEnabled}
                onSetAutoRetry={setIsAutoRetryEnabled}
                maxRetries={maxRetries}
                onSetMaxRetries={setMaxRetries}
              />
            </div>
          )}
        </div>

        {/* 하단 바: 버튼만 */}
        <div className="ws-bottom-bar" style={{ justifyContent: "flex-end" }}>
          {onClickPrev && (
            <button className="ws-btn-prev" onClick={onClickPrev}>
              이전
            </button>
          )}
          <button
            className="ws-btn-next"
            style={{ flex: "none", padding: "11px 42px" }}
            onClick={() => {
              if (worksheet.is_phonics_book && !(phonicsStepList || []).length) {
                alert("단계를 하나 이상 골라 주세요.");
                return;
              }
              if ((worksheet.unit_list || []).length > 1 && !readersSectionList.some((key) => key.startsWith("unit-"))) {
                alert("단원을 하나 이상 골라 주세요.");
                return;
              }
              onClickNext();
            }}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParagraphSelectionPage;
